import type { AdmissionDTO } from '@/types/dto/agent';

export const areAllAdmissionsWithStatus = (
  admissions: Array<AdmissionDTO>,
  status: AdmissionDTO['status']
) => admissions.every((admission) => admission.status === status);

export const areSomeAdmissionsWithStatus = (
  admissions: Array<AdmissionDTO>,
  status: AdmissionDTO['status']
) => admissions.some((admission) => admission.status === status);

export const areAllAdmissionsWithValue = <T extends keyof AdmissionDTO>(
  admissions: Array<AdmissionDTO>,
  field: T,
  value: AdmissionDTO[T]
) => admissions.every((admission) => admission[field] === value);

/**
 * Determines if a collection of admissions should be considered "refunded-like" based on their statuses.
 *
 * @param admissions - Array of admission objects to evaluate
 * @returns {boolean} True if the admissions should be considered refunded-like, false otherwise
 *
 * @example
 * // All admissions are refunded
 * refundedLike([{status: 'REFUNDED'}, {status: 'REFUNDED'}]); // true
 *
 * @example
 * // Mixed refunded and released (no fulfilled)
 * refundedLike([{status: 'REFUNDED'}, {status: 'RELEASED'}]); // true
 *
 * @example
 * // Partially refunded (some fulfilled, some refunded)
 * refundedLike([{status: 'FULFILLED'}, {status: 'REFUNDED'}]); // true
 *
 * @example
 * // Only released (no refunded, no fulfilled)
 * refundedLike([{status: 'RELEASED'}, {status: 'RELEASED'}]); // false
 */
export const areRefundedLike = (admissions: Array<AdmissionDTO>): boolean => {
  const allRefunded = areAllAdmissionsWithStatus(admissions, 'REFUNDED');
  const someRefunded = areSomeAdmissionsWithStatus(admissions, 'REFUNDED');
  const someReleased = areSomeAdmissionsWithStatus(admissions, 'RELEASED');
  const someExchanged = areSomeAdmissionsWithStatus(admissions, 'EXCHANGED');
  const someFulfilled = areSomeAdmissionsWithStatus(admissions, 'FULFILLED');
  const allEitherRefundedOrReleasedOrExchanged = admissions.every(
    (a) =>
      a.status === 'REFUNDED' ||
      a.status === 'RELEASED' ||
      a.status === 'EXCHANGED'
  );

  return (
    allRefunded ||
    (someRefunded &&
      !someFulfilled &&
      (someReleased || someExchanged) &&
      allEitherRefundedOrReleasedOrExchanged)
  );
};
